import { useCallback, useEffect, useRef } from 'react';

/** The Quote Run's page id and address (/quote-run, page-routing.ts). */
export const QUOTE_RUN_PAGE = 'quote-run';

export interface QuoteRunPageOptions {
    /** The page on screen. */
    currentPage: string;
    /** The global File Explorer's open flag. */
    explorerOpen: boolean;
    /** The live page and open flag, for callbacks that outlive a render. */
    readPage: () => string | null | undefined;
    readExplorerOpen: () => boolean;
    openExplorer: () => unknown;
    closeExplorer: () => void;
    navigate: (page: string, options?: { force?: boolean }) => void;
}

/**
 * The Quote Run is a page of its own (Peter, 2026-11-02: "not a popup"): the
 * global File Explorer is open exactly while /quote-run is the page. Arriving
 * by the menu, the address or Back opens it; leaving the page closes it; any
 * button that opens it lands on the page. The explorer itself stays mounted at
 * app level (AppFileExplorerContainer), so a run keeps going on another page.
 *
 * Returns the menu's opener: it goes to the page, and does nothing on it.
 */
export function useQuoteRunPage({
    currentPage,
    explorerOpen,
    readPage,
    readExplorerOpen,
    openExplorer,
    closeExplorer,
    navigate,
}: QuoteRunPageOptions): () => void {
    // handleNavigate is a plain per-render function (CLAUDE.md frontend rule 1),
    // so it is read through a ref, never pinned.
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;
    const openRef = useRef(openExplorer);
    openRef.current = openExplorer;
    const closeRef = useRef(closeExplorer);
    closeRef.current = closeExplorer;
    // Empty at first, so a reload that lands on /quote-run counts as arriving.
    const prevPageRef = useRef<string | null>(null);

    useEffect(() => {
        const prevPage = prevPageRef.current;
        prevPageRef.current = currentPage;
        if (currentPage === QUOTE_RUN_PAGE) {
            if (prevPage !== QUOTE_RUN_PAGE && !readExplorerOpen()) void openRef.current();
            return;
        }
        if (prevPage === QUOTE_RUN_PAGE && readExplorerOpen()) closeRef.current();
        // readExplorerOpen reads a ref; the page change alone drives this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    useEffect(() => {
        if (!explorerOpen || readPage() === QUOTE_RUN_PAGE) return;
        navigateRef.current(QUOTE_RUN_PAGE, { force: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [explorerOpen]);

    return useCallback(() => {
        if (readPage() === QUOTE_RUN_PAGE) return;
        navigateRef.current(QUOTE_RUN_PAGE);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}
